import React from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Select,
  Spinner,
  Text,
} from '@shopify/polaris';
import {
  useRecommendations,
  useRecommendationStats,
  useRegenerateRecommendations,
} from '../../hooks/useRecommendations';
import { RecommendationCard } from './RecommendationCard';
import type { RecommendationFilters } from '../../api/recommendations';

interface Props {
  auditRunId?: number;
}

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'New',          value: 'generated' },
  { label: 'Applied',      value: 'applied' },
  { label: 'Dismissed',    value: 'dismissed' },
];

const PRIORITY_OPTIONS = [
  { label: 'All priorities', value: '' },
  { label: 'High',           value: 'high' },
  { label: 'Medium',         value: 'medium' },
  { label: 'Low',            value: 'low' },
];

export function RecommendationsPanel({ auditRunId }: Props) {
  const [statusFilter,   setStatusFilter]   = React.useState('');
  const [priorityFilter, setPriorityFilter] = React.useState('');
  const [page, setPage] = React.useState(1);

  const filters: RecommendationFilters = {
    ...(auditRunId     ? { audit_run_id: auditRunId } : {}),
    ...(statusFilter   ? { status:       statusFilter }   : {}),
    ...(priorityFilter ? { priority:     priorityFilter } : {}),
    page,
    per_page: 10,
  };

  const { data, isLoading, isError } = useRecommendations(filters);
  const { data: statsData }          = useRecommendationStats();
  const { mutate: regenerate, isPending: isRegenerating } = useRegenerateRecommendations();

  const stats           = statsData?.data;
  const recommendations = data?.data ?? [];
  const meta            = data?.meta;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spinner size="small" />
      </div>
    );
  }

  if (isError) {
    return (
      <Banner tone="critical">
        <p>Failed to load recommendations. Please try again.</p>
      </Banner>
    );
  }

  return (
    <BlockStack gap="400">
      {/* Stats bar */}
      {stats && stats.total > 0 && (
        <InlineStack gap="400" wrap>
          <Text as="span" variant="bodySm" tone="subdued">
            Total: <strong>{stats.total}</strong>
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            New: <strong>{stats.by_status['generated'] ?? 0}</strong>
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            Applied: <strong>{stats.by_status['applied'] ?? 0}</strong>
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            Dismissed: <strong>{stats.by_status['dismissed'] ?? 0}</strong>
          </Text>
          {stats.total_cost_usd > 0 && (
            <Text as="span" variant="bodySm" tone="subdued">
              AI cost: <strong>${stats.total_cost_usd.toFixed(4)}</strong>
            </Text>
          )}
        </InlineStack>
      )}

      {/* Filters + Regenerate */}
      <InlineStack gap="200" wrap>
        <Select
          label="Status"
          labelInline
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
        />
        <Select
          label="Priority"
          labelInline
          options={PRIORITY_OPTIONS}
          value={priorityFilter}
          onChange={(v) => { setPriorityFilter(v); setPage(1); }}
        />
        {auditRunId && (
          <Button
            size="slim"
            loading={isRegenerating}
            onClick={() => regenerate(auditRunId)}
          >
            Regenerate
          </Button>
        )}
      </InlineStack>

      {/* List */}
      {recommendations.length === 0 ? (
        <Card>
          <Text as="p" variant="bodySm" tone="subdued">
            No AI recommendations available yet.
          </Text>
        </Card>
      ) : (
        <BlockStack gap="300">
          {recommendations.map((rec) => (
            <RecommendationCard key={rec.id} recommendation={rec} />
          ))}
        </BlockStack>
      )}

      {/* Pagination */}
      {meta && meta.last_page > 1 && (
        <InlineStack gap="200" align="center">
          <Button
            size="slim"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <Text as="span" variant="bodySm">
            Page {meta.current_page} of {meta.last_page}
          </Text>
          <Button
            size="slim"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </InlineStack>
      )}
    </BlockStack>
  );
}
